import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ApiErrorCodes, ApiErrorStatusResponse, GouvDpeDto } from '@workspace/data-types';
import { ApiService } from '@workspace/ng-http';
import { catchError, finalize, of, tap } from 'rxjs';

const ADEME_NUMBER_REGEX = /^[0-9]{4}[A-Z][0-9]{7}[A-Z]$/;

@Component({
  selector: 'etxe-property-dpe-autocomplete',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './dpe-autocomplete.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDpeAutocompleteComponent {
  private api = inject(ApiService);

  ademeNumber = model<string | undefined>('');
  readonly dpeFound = output<GouvDpeDto>();

  isLoading = signal(false);
  apiError = signal<string | null>(null);

  ademeControl = new FormControl('', [
    Validators.required,
    Validators.pattern(ADEME_NUMBER_REGEX),
    Validators.minLength(13),
    Validators.maxLength(13),
  ]);

  constructor() {
    // Sync model -> control
    effect(() => {
      const value = this.ademeNumber() ?? '';
      if (this.ademeControl.value !== value) {
        this.ademeControl.setValue(value, { emitEvent: false });
      }
    });

    // Sync control -> model
    this.ademeControl.valueChanges
      .pipe(
        tap((value) => {
          this.ademeNumber.set(value ?? '');
          this.apiError.set(null);
          if (this.ademeControl.hasError('apiError')) {
            this.ademeControl.setErrors(null);
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  fetchAdemeData() {
    const number = this.ademeControl.value?.trim();
    if (!number || this.ademeControl.invalid) {
      this.ademeControl.markAsTouched();
      return;
    }

    this.apiError.set(null);
    this.isLoading.set(true);

    this.api
      .business()
      .etxe()
      .public()
      .gouv()
      .dpe(number)
      .pipe(
        tap((data: GouvDpeDto | null) => {
          if (data) {
            this.dpeFound.emit(data);
          } else {
            this.apiError.set('Aucun DPE trouvé pour ce numéro');
            this.ademeControl.setErrors({ apiError: true });
            this.ademeControl.markAsTouched();
          }
        }),
        catchError((errorResponse) => {
          const apiError = errorResponse.error as ApiErrorStatusResponse;
          if (apiError.code === ApiErrorCodes.OBJECT_NOT_FOUND) {
            this.apiError.set('Aucun DPE trouvé pour ce numéro');
          } else {
            this.apiError.set('Une erreur est survenue lors de la récupération des données');
          }
          this.ademeControl.setErrors({ apiError: true });
          this.ademeControl.markAsTouched();
          console.log('this.apiError:', this.apiError());
          return of(null);
        }),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe();
  }
}
